// Support inbox fixtures — the Parkify-internal help@ queue.
//
// Reachable ONLY in a staff demo build (VITE_DEMO_STAFF=1, see ../constants).
// The Support screen is PARKIFY_STAFF_ONLY, so a normal sales-demo build — the
// one a rep drives on a call — never renders it and never calls these routes.
// That separation is the point: this is a QA/screenshot surface, not a demo one.
//
// ⚠️ Every person, plate, address and thread below is INVENTED. Real support
// threads are the densest customer PII we hold — names, emails, plates, journey
// times, payment ids — and demo fixtures get committed, deployed and screenshotted.
// Never seed this file from a production inbox, however convenient the realism.
// The scenarios are modelled on real incident SHAPES; the people are not real.

import { isoHoursFromNow } from '../time'
import { DEMO_STAFF_USER } from '../constants'

// The three shapes an operator needs to tell apart in the refund flow. Each
// exists because it exercises a different branch of the reason picker:
//   • validation failure — the cause Mitte's enum cannot express at all
//   • duplicate charge    — the one cause the ticket itself can evidence
//   • lookup error        — paid session our own tooling could not find
const TICKETS = [
  {
    id: 'demo-thread-validation',
    subject: 'Validation code did not come off my parking',
    from: 'Dana Whitfield',
    fromEmail: 'dana.whitfield@example.com',
    snippet: 'The restaurant gave me a validation but I was still charged full price.',
    body:
      'Hi — I parked at the Lenox deck on Sunday and the host desk gave me a '
      + 'validation code for my meal. I entered it but the app still charged me '
      + '$10.99. The code was definitely valid, they printed it in front of me.',
    receivedAt: isoHoursFromNow(-5),
    lastMessageAt: isoHoursFromNow(-5),
    isUnread: true,
    labels: ['INBOX'],
    messageCount: 1,
    latestMessageId: 'demo-msg-validation-1',
  },
  {
    id: 'demo-thread-duplicate',
    subject: 'Charged twice for one visit',
    from: 'Ray Okonkwo',
    fromEmail: 'r.okonkwo@example.com',
    snippet: 'Two charges, same amount, same afternoon.',
    body:
      'There are two identical $14.99 charges on my card from the same lot on '
      + 'the same afternoon. I only parked once.',
    receivedAt: isoHoursFromNow(-27),
    lastMessageAt: isoHoursFromNow(-27),
    isUnread: false,
    labels: ['INBOX'],
    messageCount: 1,
    latestMessageId: 'demo-msg-duplicate-1',
  },
  {
    id: 'demo-thread-lookup',
    subject: 'I paid but got a notice anyway',
    from: 'Priya Raman',
    fromEmail: 'priya.raman@example.com',
    snippet: 'Paid on my phone, still received a notice.',
    body:
      'I paid for two hours through the QR code and still found a notice on my '
      + 'windscreen. I have the card statement if that helps.',
    receivedAt: isoHoursFromNow(-49),
    lastMessageAt: isoHoursFromNow(-49),
    isUnread: false,
    labels: ['INBOX'],
    messageCount: 1,
    latestMessageId: 'demo-msg-lookup-1',
    // A forward went out from this ticket to the booting vendor; the vendor's
    // answer is the ticket below. Both sides of the link are in the fixture so
    // a demo build shows the real shipped shape rather than half of it.
    linkedReplyThreadIds: ['demo-thread-enforcement-reply'],
  },
  // Refunded by hand in the Mitte console, so none of our own refund records
  // know (WP4, 2026-09-23). The shape behind the "DO NOT issue another refund,
  // it was already refunded in Mitte" operator notes; the people are invented.
  {
    id: 'demo-thread-mitte-refunded',
    subject: 'Still waiting on my refund',
    from: 'Morgan Ellery',
    fromEmail: 'morgan.ellery@example.com',
    snippet: 'Was told I would be refunded for the double charge. Any update?',
    body:
      'Hi, I was charged $12.49 for parking I cancelled and someone said it would '
      + 'be refunded. I have not heard anything since. Can you check?',
    receivedAt: isoHoursFromNow(-2),
    lastMessageAt: isoHoursFromNow(-2),
    isUnread: true,
    labels: ['INBOX'],
    messageCount: 1,
    latestMessageId: 'demo-msg-mitte-refunded-1',
  },
  // A wrong-duration courtesy refund (2026-09-25 shape). Gmail's display name
  // is surname-first ("Quillfeather Rowan") while the parker signs "Rowan
  // Quillfeather", Avery escalated with NO draft, and the only session is a
  // Mitte one under "Sessions on this plate". The operator writes their own
  // reply and refunds that session. Invented person.
  {
    id: 'demo-thread-duration-courtesy',
    subject: 'Quillfeather: Parking Issue',
    from: 'Quillfeather Rowan',
    fromEmail: 'rowan.q@example.com',
    snippet: 'I picked 6am to 9am instead of 6pm to 9pm. Could I get the extra hours back?',
    body:
      'To whomever it concerns,\n\nI picked the wrong end time at checkout, 9:00am '
      + 'instead of 9:00pm, and paid for far more than I needed. Could the extra hours '
      + 'be refunded?\n\nRowan Quillfeather',
    receivedAt: isoHoursFromNow(-720),
    lastMessageAt: isoHoursFromNow(-719),
    isUnread: false,
    labels: ['INBOX'],
    messageCount: 1,
    latestMessageId: 'demo-msg-duration-courtesy-1',
  },
  // The enforcement partner's reply. It is a SEPARATE ticket because it is a
  // separate email conversation — a forward to a vendor is a fresh email, not a
  // reply in the parker's thread. Before reply-thread linkage shipped there was
  // nothing tying this back, and an operator could only find the source ticket
  // by already knowing the vendor contact or the plate to search for.
  {
    id: 'demo-thread-enforcement-reply',
    subject: 'Re: Notice dispute — plate DMO8823 at Demo Deck — Lenox [PK-D3M0L1NK]',
    from: 'Empire Parking Services',
    fromEmail: 'management@empireparkingservices.example',
    snippet: 'Notice voided. Nothing further needed on your side.',
    body:
      'Thanks for the paid-session confirmation. We have voided the notice on '
      + 'plate DMO8823 for that date. Nothing further needed on your side.',
    receivedAt: isoHoursFromNow(-3),
    lastMessageAt: isoHoursFromNow(-3),
    isUnread: true,
    labels: ['INBOX'],
    messageCount: 1,
    latestMessageId: 'demo-msg-enforcement-reply-1',
    linkedSourceThreadId: 'demo-thread-lookup',
    linkedKind: 'enforcement' as const,
  },
  // A phone call whose caller gave no email (WP3). The From is the synthetic
  // placeholder parkify-cs generates, which delivers nowhere, so every composer
  // must disable Send and lead with "Callback required: <number>". Invented
  // caller; the number is in the 555-01xx fictional range.
  {
    id: 'demo-thread-voice-callback',
    subject: '📞 Voice call — Jordan Vale — Receipt',
    from: '15555550142',
    fromEmail: '15555550142@voice-callers.parkify.com',
    snippet: 'Avery (voice assistant) collected this intake: Name: Jordan Vale',
    body:
      'Inbound phone call to Parkify support. Avery (voice assistant) collected this intake:\n'
      + 'Name: Jordan Vale\n'
      + 'Phone: +15555550142\n'
      + 'Email: (none captured — call back at +15555550142)\n'
      + 'License plate: DMO5150\n'
      + 'Lot: Demo Deck — Lenox (DM240)\n'
      + 'Category: Receipt\n\n'
      + 'What they need:\n'
      + 'Wants a receipt for Monday and asked how to pay next time.\n',
    receivedAt: isoHoursFromNow(-49),
    lastMessageAt: isoHoursFromNow(-49),
    isUnread: true,
    labels: ['INBOX'],
    messageCount: 1,
    latestMessageId: 'demo-msg-voice-callback-1',
  },
  // A phone caller whose spelled address was recorded with a space in it, who
  // texted a different first name, and whose one reply bounced (the shape of
  // help@ 1a0cbbed7d72f3e9, 2026-09-25; the person is invented). Shows Avery's
  // address recovery: the bounce, ranked guesses, and the re-listen check.
  {
    id: 'demo-thread-voice-bounced',
    subject: '📞 Voice call — Kasey Norrow — General',
    from: 'Kasey Norrow',
    fromEmail: '15555550199@voice-callers.parkify.com',
    snippet: 'Avery (voice assistant) collected this intake: Name: Kasey Norrow',
    body:
      'Inbound phone call to Parkify support. Avery (voice assistant) collected this intake:\n'
      + 'Name: Kasey Norrow\n'
      + 'Phone: +15555550199\n'
      + 'Email: casey morrow@example.com ⚠️ NOT CONFIRMED — caller stated but did not verify letter-by-letter\n'
      + 'License plate: 3MO5150\n'
      + 'Lot: Demo Deck — Lenox (DM240)\n'
      + 'Category: other\n\n'
      + 'What they need:\n'
      + 'The caller entered the wrong license plate number for their parking session.\n',
    receivedAt: isoHoursFromNow(-60),
    lastMessageAt: isoHoursFromNow(-1),
    isUnread: true,
    labels: ['INBOX'],
    messageCount: 3,
    latestMessageId: 'demo-msg-voice-bounced-3',
    voiceEmailConfirmed: false,
    voiceRejectedEmails: [{ email: 'casey morrow@example.com', reason: 'contains a space' }],
    voiceBouncedEmails: ['caseymorrow@example.com'],
  },
]

// Avery's address recovery for the bounced phone ticket above
// (GET /api/support/email-recovery). Every other ticket reads as confirmed,
// which renders nothing.
const EMAIL_RECOVERY: Record<string, unknown> = {
  'demo-thread-voice-bounced': {
    status: 'bounced',
    bounced: [{ email: 'caseymorrow@example.com', at: isoHoursFromNow(-1), kind: 'permanent', detail: '550 5.5.0 Requested action not taken: mailbox unavailable' }],
    spelled: 'caseymorrow@example.com',
    names: { spoken: ['Kasey Norrow'], texted: ['Casey'] },
    candidates: [
      { email: 'casey.morrow@example.com', rank: 1, source: 'separator', why: ['as spelled, with a dot where the caller paused'] },
      { email: 'caseymorrov@example.com', rank: 2, source: 'letter_confusion', why: ['"morrow" with W→V, letters that sound alike when spelled'] },
      { email: 'caseynorrow@example.com', rank: 3, source: 'letter_confusion', why: ['"morrow" with M→N, letters that sound alike when spelled', '"norrow" matches the name heard on the call'] },
      { email: 'casey.norrow@example.com', rank: 4, source: 'name', why: ['first name "casey" texted by the caller', 'surname "norrow" heard on the call', 'with a dot'] },
      { email: 'casey_morrow@example.com', rank: 5, source: 'separator', why: ['as spelled, with an underscore where the caller paused'] },
    ],
    guidance: 'These are guesses. Call the caller back to confirm one, or pick one and send through Override: every send needs a person to approve the exact address, because a wrong guess reaches a stranger.',
    resolveRisk: { code: 'reply_bounced', message: 'Our reply bounced (caseymorrow@example.com), so the caller has heard nothing. Call them back or send to a confirmed address before resolving.' },
  },
}

export function demoEmailRecovery(threadId: string) {
  return EMAIL_RECOVERY[threadId]
    ?? { status: 'confirmed', bounced: [], names: { spoken: [], texted: [] }, candidates: [], guidance: '', resolveRisk: null }
}

export function demoRelisten(body: Record<string, unknown> | null) {
  if (body?.threadId !== 'demo-thread-voice-bounced') {
    return { available: false, segments: [], error: 'no recording is linked to this call' }
  }
  return {
    available: true,
    intakeId: 9001,
    segments: [
      { segment: 'email', start: 23, end: 38, intakeValue: 'casey morrow@example.com', firstPassText: 'C-A-S-E-Y M-O-R-R-O-W @example.com.', secondPassText: 'C A S E Y N O R R O W at example.com', secondPassValue: 'caseynorrow@example.com', agrees: false },
      { segment: 'plate', start: 41, end: 50, intakeValue: '3MO5150', firstPassText: '3MO5150.', secondPassText: 'D M O five one five zero', secondPassValue: 'DMO5150', agrees: false },
    ],
  }
}

// ── Ownership (WP7) ─────────────────────────────────────────────────────────
// Who owns each ticket, and the follow-up promise one of them still owes, held
// in memory so assigning, Mark kept and Release behave in a demo build the way
// they do live (the console re-syncs the inbox after an assignment). Invented
// teammates, like everything else in this file.
const DEMO_ASSIGNEES = [
  { email: DEMO_STAFF_USER.email, name: DEMO_STAFF_USER.name, role: DEMO_STAFF_USER.role },
  { email: 'riley.demo@parkify.com', name: 'Riley Demo', role: 'support' },
  { email: 'sam.demo@parkify.com', name: 'Sam Demo', role: 'admin' },
]

const OWNERSHIP: Record<string, { assignee: string; assignedAt: string; assignedBy: string }> = {
  'demo-thread-duplicate': { assignee: 'riley.demo@parkify.com', assignedAt: isoHoursFromNow(-20), assignedBy: 'sam.demo@parkify.com' },
  'demo-thread-lookup': { assignee: 'riley.demo@parkify.com', assignedAt: isoHoursFromNow(-47), assignedBy: 'sam.demo@parkify.com' },
}

// A promise made two days ago and never delivered: past its 24h window, paged
// once per level, and exactly the row the daily digest lists.
const COMMITMENTS: Record<string, {
  promisedAtIso: string; dueAtIso: string; promiseText: string; assignee: string; ordinal: number; breachPagedLevel?: number
}> = {
  'demo-thread-lookup': {
    promisedAtIso: isoHoursFromNow(-48),
    dueAtIso: isoHoursFromNow(-24),
    promiseText: "We've asked the enforcement partner to void the notice. A teammate will follow up here within 24 hours.",
    assignee: 'riley.demo@parkify.com',
    ordinal: 1,
    breachPagedLevel: 2,
  },
}

export function demoSupportTickets() {
  const tickets = TICKETS.map(t => ({
    ...t,
    ...(OWNERSHIP[t.id] ?? {}),
    ...(COMMITMENTS[t.id] ? { openCommitment: COMMITMENTS[t.id] } : {}),
  }))
  return { tickets, source: 'demo', total: tickets.length, archivedWaiting: 0 }
}

export function demoSupportAssignees() {
  return { assignees: DEMO_ASSIGNEES }
}

export function demoSupportAssign(body: { threadId?: string; assignee?: string | null } | undefined) {
  const threadId = body?.threadId ?? ''
  const email = (body?.assignee ?? '').trim().toLowerCase()
  if (!email) {
    delete OWNERSHIP[threadId]
  } else {
    if (!DEMO_ASSIGNEES.some(a => a.email === email)) {
      return { ok: false, error: `Cannot assign ${email}: no ops console user with that email` }
    }
    OWNERSHIP[threadId] = { assignee: email, assignedAt: new Date().toISOString(), assignedBy: DEMO_STAFF_USER.email }
  }
  if (COMMITMENTS[threadId]) COMMITMENTS[threadId].assignee = email || 'riley.demo@parkify.com'
  const o = OWNERSHIP[threadId]
  return {
    ok: true,
    assignee: o?.assignee ?? null, assignedAt: o?.assignedAt ?? null, assignedBy: o?.assignedBy ?? null,
    commitment: COMMITMENTS[threadId] ?? null,
  }
}

export function demoSupportCommitmentClose(body: { threadId?: string } | undefined) {
  const threadId = body?.threadId ?? ''
  const had = COMMITMENTS[threadId]
  delete COMMITMENTS[threadId]
  return had ? { ok: true, commitment: had } : { ok: false, error: 'No open commitment on this ticket' }
}

// Per-thread analysis, keyed by threadId — the shape POST /api/support/triage
// returns (a Partial<SupportTicket> the card merges onto the ticket).
//
// NOTE none of these sets a cause code. Avery does not pick one and neither does
// this fixture: the reason picker is operator-asserted, and pre-filling it here
// would make a demo build show a state the real console cannot produce.
const MITTE_REFUNDED_AT = isoHoursFromNow(-30)

const TRIAGE: Record<string, Record<string, unknown>> = {
  // Mitte shows the refund, our D1 records show none. The triage backend
  // confirms it (refund-on-record.ts) instead of promising another one.
  'demo-thread-mitte-refunded': {
    aiSummary: 'Customer asks about a refund for a $12.49 charge. Mitte shows it already refunded.',
    aiAction: 'reply',
    aiConfidence: 'high',
    aiRecommendation:
      'Refunded in Mitte: $12.49 of $12.49. Do NOT issue another refund: the refund button is '
      + 'locked for this payment. Confirm the existing refund to the customer with its date.',
    draftReply:
      'Thanks for reaching out. We checked this charge and your refund of $12.49 was issued '
      + 'to the card you paid with. Depending on your bank, it can take 3 to 5 business days '
      + 'to show on your statement.\n\nParkify Support',
    matchedTxnId: 'demo-txn-mitte-refunded',
    matchedPlate: 'DMO5512',
    matchedZone: 'Demo Deck — Lenox',
    matchedTxnVerified: true,
    matchedAmountCents: 1249,
    matchedSessions: [{
      txnId: 'demo-txn-mitte-refunded', plate: 'DMO5512', zoneName: 'Demo Deck — Lenox',
      paidAt: isoHoursFromNow(-54), amountCents: 0,
      role: 'refund_target', paymentId: 'demo_pay_mitte_refunded', placeId: 'demo_place_lenox',
      grossAmountCents: 1249, refundedAmountCents: 1249, hasRefunds: true,
      refundedAt: MITTE_REFUNDED_AT, refundKnown: true, source: 'mitte',
    }],
  },
  // Escalated with no draft: a picked-wrong duration is not refundable by
  // policy, so Avery leaves the reply to the operator (courtesy is their call).
  'demo-thread-duration-courtesy': {
    aiSummary: 'Parker chose a 9am end time instead of 9pm and asks for the extra hours back.',
    aiAction: 'escalate',
    aiConfidence: 'medium',
    aiConfidencePct: 55,
    aiRecommendation:
      'A duration picked at checkout is not refundable by policy. Any refund here is a courtesy, operator call.',
    draftReply: '',
    matchedPlate: 'DMO7731',
    matchedZone: 'Demo Harbor Lot',
  },
  'demo-thread-validation': {
    aiSummary:
      'Merchant validation exists for this session but never attached — parker '
      + 'paid the full $10.99. Lot-level pattern: third this week at this zone.',
    aiAction: 'refund',
    aiConfidence: 'high',
    aiRecommendation:
      'Refund in full. The validation is present in the merchant record, so this '
      + 'is our defect rather than a goodwill call.',
    draftReply:
      "Hi Dana,\n\nYou're right — your validation didn't come off the session, and "
      + "that's our error. I've refunded the full $10.99 back to your card; it "
      + 'usually lands within 5–10 business days.\n\nSorry for the trouble.',
    matchedTxnId: 'demo-txn-validation',
    matchedPlate: 'DMO4417',
    matchedZone: 'Demo Deck — Lenox',
    matchedTxnVerified: true,
    matchedAmountCents: 1099,
    recommendedRefundCents: 1099,
    matchedSessions: [{
      txnId: 'demo-txn-validation', plate: 'DMO4417', zoneName: 'Demo Deck — Lenox',
      paidAt: isoHoursFromNow(-30), amountCents: 1099,
      role: 'refund_target', paymentId: 'demo_pay_validation', placeId: 'demo_place_lenox',
    }],
  },
  'demo-thread-duplicate': {
    aiSummary: 'Two identical charges on one plate, same zone, 90 seconds apart.',
    aiAction: 'refund',
    aiConfidence: 'high',
    aiRecommendation: 'Refund the later of the two charges.',
    draftReply:
      "Hi Ray,\n\nYou were charged twice for one visit — I've refunded the "
      + 'duplicate $14.99.',
    matchedTxnId: 'demo-txn-duplicate-b',
    matchedPlate: 'DMO8820',
    matchedZone: 'Demo Surface Lot — East',
    matchedTxnVerified: true,
    matchedAmountCents: 1499,
    recommendedRefundCents: 1499,
    // A detected duplicate is the one cause buildRefundRequest can infer on its
    // own, so this ticket shows the picker's "not stated" default still filing
    // correctly.
    duplicateGroup: {
      detected: true, reason: 'same plate/zone/amount within 2 minutes',
      keep: 'demo-txn-duplicate-a', refund: 'demo-txn-duplicate-b',
    },
    matchedSessions: [{
      txnId: 'demo-txn-duplicate-b', plate: 'DMO8820', zoneName: 'Demo Surface Lot — East',
      paidAt: isoHoursFromNow(-52), amountCents: 1499,
      role: 'refund_target', paymentId: 'demo_pay_duplicate', placeId: 'demo_place_east',
    }],
  },
  'demo-thread-lookup': {
    aiSummary:
      'Parker says they paid; no session found on the plate. Card statement '
      + 'suggests the charge exists but our lookup cannot reach it.',
    // Deliberately NOT 'refund': this is the ticket where the manual override
    // button renders, which is the other path the reason picker feeds.
    aiAction: 'escalate',
    aiConfidence: 'low',
    aiRecommendation:
      'Ask for the last four digits and the charge timestamp before refunding — '
      + 'the session may be under a mistyped plate.',
    draftReply:
      "Hi Priya,\n\nCould you send the last four digits of the card and the time "
      + 'of the charge? I want to find the session before I do anything else.',
    matchedTxnId: 'demo-txn-lookup',
    matchedPlate: 'DMO2093',
    matchedZone: 'Demo Deck — Riverside',
    matchedTxnVerified: true,
    matchedAmountCents: 1099,
    recommendedRefundCents: 1099,
    matchedSessions: [{
      txnId: 'demo-txn-lookup', plate: 'DMO2093', zoneName: 'Demo Deck — Riverside',
      paidAt: isoHoursFromNow(-74), amountCents: 1099,
      role: 'refund_target', paymentId: 'demo_pay_lookup', placeId: 'demo_place_riverside',
    }],
  },
  // What the Worker returns for the placeholder ticket above after WP1 + WP3:
  // escalate, "Callback required" leading the recommendation, the draft as a
  // phone/SMS script, and (the call being 49h old) its pay link stripped.
  'demo-thread-voice-bounced': {
    aiSummary: 'Caller entered the wrong plate at Demo Deck — Lenox. The one reply we sent bounced; the caller address is still unknown.',
    aiAction: 'escalate',
    aiConfidence: 'medium',
    aiConfidencePct: 60,
    aiRecommendation:
      'Callback required: +15555550199. The reply to caseymorrow@example.com bounced. See the address recovery below the To: line for likely alternatives.',
    draftReply:
      'Hi Casey,\n\nThanks for calling about the plate on your parking at Demo Deck — Lenox. '
      + 'Could you reply with the correct plate for your car?',
    matchedPlate: 'DMO5150',
    matchedZone: 'Demo Deck — Lenox',
  },
  'demo-thread-voice-callback': {
    aiSummary: 'Caller wants a receipt for Monday at Demo Deck — Lenox and asked how to pay next time.',
    aiAction: 'escalate',
    aiConfidence: 'medium',
    aiConfidencePct: 70,
    aiRecommendation:
      'Callback required: +15555550142. The From address is an undeliverable voice placeholder, so '
      + 'nothing sent to it reaches the caller. Payment link removed: the call was 49h ago (over 6h), so '
      + 'the caller has likely left the lot. Ask whether they are still parked before offering any link.',
    draftReply:
      '[Phone/SMS script — read to the caller at +15555550142; do not email, the address is a placeholder]\n\n'
      + 'Hi Jordan, this is Parkify Support returning your call about Monday at Demo Deck — Lenox. '
      + 'Could you confirm your plate letter by letter so we can send your receipt? '
      + 'What is the best email for it?',
    matchedPlate: 'DMO5150',
    matchedZone: 'Demo Deck — Lenox',
  },
}

// Per-thread plateResults (the researched sessions the card lists). Only the
// Mitte-refunded thread carries one: it is what shows the refund's date.
const PLATE_RESULTS: Record<string, unknown[]> = {
  'demo-thread-duration-courtesy': [{
    plate: 'DMO7731', zone: 'Demo Harbor Lot', dateRange: 'last 365 days',
    totalSessions: 1, totalPaidUsd: 70.8, activeSessions: 0, scope: 'plate',
    sessions: [{
      txnId: 'demo-txn-duration-courtesy', plate: 'DMO7731', zoneName: 'Demo Harbor Lot',
      durationLabel: '14h 51m', amountCents: 7080, paidAt: isoHoursFromNow(-721), expiresAt: isoHoursFromNow(-706),
      isActive: false, paymentId: 'demo_pay_duration_courtesy', placeId: 'demo_place_harbor',
      grossAmountCents: 7080, refundedAmountCents: 0, hasRefunds: false, source: 'mitte',
    }],
  }],
  'demo-thread-mitte-refunded': [{
    plate: 'DMO5512', zone: 'Demo Deck — Lenox', dateRange: 'last 365 days',
    totalSessions: 1, totalPaidUsd: 0, activeSessions: 0, scope: 'plate',
    sessions: [{
      txnId: 'demo-txn-mitte-refunded', plate: 'DMO5512', zoneName: 'Demo Deck — Lenox',
      durationLabel: '2h', amountCents: 0, paidAt: isoHoursFromNow(-54), expiresAt: isoHoursFromNow(-52),
      isActive: false, paymentId: 'demo_pay_mitte_refunded', placeId: 'demo_place_lenox',
      grossAmountCents: 1249, refundedAmountCents: 1249, hasRefunds: true,
      refundedAt: MITTE_REFUNDED_AT, refundKnown: true, source: 'mitte',
    }],
  }],
}

export function demoSupportTriage(body: Record<string, unknown> | null) {
  const threadId = typeof body?.threadId === 'string' ? body.threadId : ''
  const triage = TRIAGE[threadId] ?? null
  const plateResults = PLATE_RESULTS[threadId]
  return { ok: true, triage, source: 'demo', refineHistory: [], ...(plateResults ? { plateResults } : {}) }
}

// Writes. Every one returns the benign success the UI expects and touches
// nothing — a demo build has no backend to touch. The refund route in
// particular must never look like it moved money beyond the console's own
// optimistic update.
export function demoSupportRefund(body: Record<string, unknown> | null) {
  const amount = typeof body?.amount === 'number' ? body.amount : 0
  return {
    ok: true,
    refund: { id: 'demo_refund_stub', amount },
    alreadyRefunded: false,
    replySent: true, replyError: null,
    emailSent: true, emailError: null,
  }
}

export function demoSupportAutoReplySettings() {
  // Autonomous sending OFF in the demo: a fixture that shows it armed invites
  // the reader to believe this build could send to a real customer.
  return { enabled: false, mode: 'draft_only', updatedAt: isoHoursFromNow(-240) }
}

// Knowledge-base candidates — Avery proposing KB entries from resolved threads.
// The widget polls this on the Support screen itself, so a staff build hits it
// immediately; an empty pending list is the honest quiet state.
export function demoSupportKbCandidates() {
  return { candidates: [], total: 0 }
}
